/**
 * Parses health profile item.data which may be double- or triple-escaped
 * (e.g. "\"7\"" or "\"\\\"NO\\\"\""). Returns a plain value or parsed object.
 */
export function parseHealthProfileData(
  data: string | null | undefined
): unknown {
  if (data == null || data === "") return null;
  let current: unknown = data;
  for (;;) {
    if (typeof current !== "string") return current;
    try {
      const next = JSON.parse(current);
      // If result is a quoted JSON string (e.g. "\"7\""), unwrap one more level
      if (
        typeof next === "string" &&
        next.length >= 2 &&
        next.startsWith('"') &&
        next.endsWith('"')
      ) {
        current = next;
      } else if (
        typeof next === "string" &&
        (next.trim().startsWith("{") || next.trim().startsWith("["))
      ) {
        // String that looks like JSON object/array (e.g. from double-escaped API data) – parse to object/array
        try {
          current = JSON.parse(next);
        } catch {
          return next;
        }
      } else {
        return next;
      }
    } catch {
      return current;
    }
  }
}

/**
 * Get string value from item.data (handles escaped strings).
 */
export function parseHealthProfileDataAsString(
  data: string | null | undefined
): string | null {
  const parsed = parseHealthProfileData(data);
  if (parsed == null) return null;
  if (typeof parsed === "string") return parsed;
  if (typeof parsed === "number") return String(parsed);
  return null;
}

/**
 * Get number value from item.data, or null if the data is not a number
 */
export function parseHealthProfileDataAsNumber(
  data: string | null | undefined
): number | null {
  const parsed = parseHealthProfileData(data);
  if (parsed == null) return null;
  if (typeof parsed === "number") return parsed;
  if (typeof parsed === "string") {
    const n = Number(parsed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Get object with value/unit from item.data (for metrics like { value, unit }).
 */
export function parseHealthProfileDataAsValueUnit(
  data: string | null | undefined
): { value?: string; unit?: string } | null {
  const parsed = parseHealthProfileData(data);
  if (parsed == null) return null;
  if (typeof parsed === "object" && parsed !== null && "value" in parsed) {
    const o = parsed as Record<string, unknown>;
    return {
      value: o.value != null ? String(o.value) : undefined,
      unit: o.unit != null ? String(o.unit) : undefined,
    };
  }
  return null;
}
